import { API_CONFIG } from '../../constants/api.constants';
import { API_ERROR_MESSAGES } from '../../constants/messages.constant';

export interface FetchJsonOptions extends RequestInit {
  /**
   * Aborts the request once this many ms elapse.
   * @default API_CONFIG.DEFAULT_TIMEOUT
   */
  timeout?: number;

  /**
   * Reused instead of creating one internally, so a caller can hold onto it
   * and abort the request from outside (e.g. on unmount, or a superseded cycle).
   */
  controller?: AbortController;
}

/**
 * Fetches `url` and parses its JSON body, aborting if `timeout` elapses.
 * Throws a normalized `Error` for a non-OK response; a network failure or an
 * abort propagates as-is so callers can tell them apart from a bad response.
 */
export const fetchJson = async <T>(
  url: string,
  {
    timeout = API_CONFIG.DEFAULT_TIMEOUT,
    controller = new AbortController(),
    ...fetchOptions
  }: FetchJsonOptions = {}
): Promise<T> => {
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `${API_ERROR_MESSAGES.FETCH_FAILED}: ${response.status}`
      );
    }

    return (await response.json()) as T;
  } finally {
    clearTimeout(timeoutId);
  }
};
